import { Component, type ErrorInfo, type ReactNode } from 'react';
import { Box, Button, Typography } from '@mui/material';
import ReportProblemOutlinedIcon from '@mui/icons-material/ReportProblemOutlined';

interface Props {
  children: ReactNode;
}
interface State {
  error: Error | null;
}

/** Catches render errors so a single broken screen can't take down the console. */
export class ErrorBoundary extends Component<Props, State> {
  state: State = { error: null };

  static getDerivedStateFromError(error: Error): State {
    return { error };
  }

  componentDidCatch(error: Error, info: ErrorInfo): void {
    console.error('Render error', error, info.componentStack);
  }

  render(): ReactNode {
    if (!this.state.error) return this.props.children;
    return (
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          gap: 1.5,
          minHeight: 320,
          p: 4,
          textAlign: 'center',
        }}
      >
        <ReportProblemOutlinedIcon color="error" sx={{ fontSize: 44 }} />
        <Typography variant="h3">This screen hit an error</Typography>
        <Typography variant="body2" color="text.secondary" sx={{ maxWidth: 480 }}>
          The view failed to render. Reloading usually clears it. If it keeps happening, note the
          time and let an administrator know.
        </Typography>
        <Button variant="contained" onClick={() => this.setState({ error: null })}>
          Try again
        </Button>
      </Box>
    );
  }
}
